import { Component, Event, EventEmitter, getAssetPath, h, Host, State } from '@stencil/core';

import wcShared from '@web-components/shared';

import { INews } from '../../../models/typing';
import { NewsService } from '../../../services/news.service';
import newsStore from '../../../stores/news.store';

@Component({
  tag: 'recent-news',
  styleUrl: 'recent-news.scss',
  shadow: true,
})
export class RecentNews {
  @State() scrollLoading = false;
  @State() filterCondition: (itemNews) => boolean = null;

  @Event() handleNewsEvent: EventEmitter<{ unseenNewsCount: number; toClose: boolean }>;

  private recentNewsElement!: HTMLElement;
  private nodeInfiniteScroll: HTMLElement;

  private isLoading = () => !newsStore.recent || newsStore.recent.length === 0;

  componentDidLoad() {
    wcShared?.store?.addObserver('langChange', (_: string) => {
      newsStore.recent = [];
      newsStore.page = 1;
      this.getRecentNews();
    });
  }

  componentDidRender() {
    const option = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver(this.observerCallback, option);
    if (Boolean(this.nodeInfiniteScroll)) observer.observe(this.nodeInfiniteScroll);
  }

  private observerCallback = (entries) => {
    const target = entries[0];
    if (
      target.isIntersecting &&
      target.target.classList.contains('node-scroll') &&
      newsStore.pages !== newsStore.page &&
      newsStore.recent &&
      !this.scrollLoading
    ) {
      this.scrollLoading = true;
      newsStore.page = newsStore.page + 1;
      if (this.getNews().length > 0) this.getRecentNews();
    }
  };

  private getRecentNews() {
    NewsService.getNews().then(() => {
      if (!newsStore.recent) newsStore.recent = [];
      this.scrollLoading = false;
    });
  }

  private postSeenAll() {
    NewsService.postAllNews().then((r) => {
      if (r.result === 'success') {
        newsStore.recent = newsStore.recent.map((item) => {
          return { ...item, seen: true };
        });
        this.handleNewsEvent.emit({ unseenNewsCount: 0, toClose: false });
      }
    });
  }

  private scrollToHeader() {
    this.recentNewsElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  }

  private noItem = () => {
    return (
      <div class="no-item">
        <img
          src={getAssetPath('./../assets/icons/no-news.svg')}
          alt="no-news-icon"
        />
        <div>
          {Boolean(this.filterCondition)
            ? wcShared?.translate('Aucune actualité non lue')
            : wcShared?.translate('Restez connecte(e) pour decouvrir les prochaines nouveautes !')}
        </div>
        {Boolean(this.filterCondition) && (
          <span onClick={() => (this.filterCondition = null)}>
            {wcShared.translate('Effacer le filtre')}
          </span>
        )}
      </div>
    );
  };

  private getNews() {
    return newsStore.recent?.filter(this.filterCondition || (() => true));
  }

  private itemSkeleton = () => {
    return (
      <div class="skeleton">
        <div class="skeleton--info" />
        <div class="skeleton--title">
          <div class="skeleton--title--avatar" />
          <div class="skeleton--title--title" />
        </div>
        <div class="skeleton--content" />
        <div class="skeleton--link" />
        <div class="skeleton--image" />
        <div class="skeleton--date" />
      </div>
    );
  };

  render() {
    return (
      <Host>
        <div
          class="recent-news"
          ref={(el) => (this.recentNewsElement = el as HTMLElement)}
        >
          <div class="recent-news__header">
            <div class="container">
              <div
                class="container__title"
                onClick={() => this.scrollToHeader()}
              >
                {Boolean(this.filterCondition)
                  ? wcShared.translate('Actualités non lues')
                  : wcShared?.translate('Actualites')}
                {newsStore.unseenNewsCount > 0 && <span>{newsStore.unseenNewsCount}</span>}
              </div>
              {newsStore.unseenNewsCount > 0 && (
                <div
                  class="all-read-link"
                  onClick={() => this.postSeenAll()}
                >
                  {wcShared?.translate('Tout marquer comme lu')}
                </div>
              )}

              <svg
                width="16px"
                height="10px"
                viewBox="0 0 16 10"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => {
                  if (!this.filterCondition) this.filterCondition = (news: INews) => !news.seen;
                  else this.filterCondition = null;
                }}
                class={{
                  ['container__action']: true,
                  active: Boolean(this.filterCondition),
                }}
              >
                <path
                  d="M0 1C0 0.723858 0.223858 0.5 0.5 0.5H15.5C15.7761 0.5 16 0.723858 16 1C16 1.27614 15.7761 1.5 15.5 1.5H0.5C0.223858 1.5 0 1.27614 0 1Z"
                  fill="currentColor"
                />
                <path
                  d="M1.5 5C1.5 4.72386 1.72386 4.5 2 4.5L14 4.5C14.2761 4.5 14.5 4.72386 14.5 5C14.5 5.27614 14.2761 5.5 14 5.5L2 5.5C1.72386 5.5 1.5 5.27614 1.5 5Z"
                  fill="currentColor"
                />
                <path
                  d="M4 8.5C3.72386 8.5 3.5 8.72386 3.5 9C3.5 9.27614 3.72386 9.5 4 9.5L12 9.5C12.2761 9.5 12.5 9.27614 12.5 9C12.5 8.72386 12.2761 8.5 12 8.5L4 8.5Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </div>
          {this.isLoading() || this.getNews().length > 0 ? (
            this.isLoading() ? (
              this.itemSkeleton()
            ) : (
              this.getNews().map((item, index) => {
                return (
                  <item-news
                    key={index}
                    item={item}
                  />
                );
              })
            )
          ) : (
            <this.noItem />
          )}
          <div
            class={{ 'node-scroll': true, 'end-scroll': newsStore.pages === newsStore.page }}
            ref={(el) => (this.nodeInfiniteScroll = el as HTMLElement)}
          />
        </div>
      </Host>
    );
  }
}
